import React from 'react';

import { link, routes } from '@aline/routes';
import { Helmet } from 'react-helmet';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ContractTypesTab, RolesTab } from 'src/components';
import { GroupsPage, UsersList } from 'src/components/admin';
import { createRelativeRouter } from 'src/helpers/router-helpers';
import { makeStyles } from 'tss-react/mui';

import { AdminOnlyRoute } from './AdminOnlyRoute';
import { AdminSidebar } from './AdminSidebar';
import { ApiKeysPage } from './api-keys';
import { BillingPage } from './billing';
import { DataMigrationPage } from './data-migration/DataMigrationPage';
import { IntegrationsList } from './IntegrationsList';
import { OrgDetails } from './org';
import { UpgradeRequestsPage } from './UpgradeRequestsPage';
import { UsagePage } from './usage';

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    minWidth: theme.breakpoints.values.md,
    overflow: 'hidden',
  },
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
    overflow: 'auto',
    padding: theme.spacing(1),
  },
}));

const { toRelativePath: toRelativeAdminPath } = createRelativeRouter(routes.AdminIndex, {
  nestedRoutes: true,
});

export function Admin(): JSX.Element {
  const { classes } = useStyles();

  return (
    <>
      <Helmet>
        <title>Admin</title>
      </Helmet>
      <div className={classes.container}>
        <AdminSidebar />
        <div className={classes.content}>
          <Routes>
            <Route element={<OrgDetails />} path={toRelativeAdminPath(routes.AdminOrgIndex)} />
            <Route element={<ApiKeysPage />} path={toRelativeAdminPath(routes.AdminApiKeys)} />
            <Route element={<UsersList />} path={toRelativeAdminPath(routes.AdminUserIndex)} />
            <Route element={<GroupsPage />} path={toRelativeAdminPath(routes.AdminGroupIndex)} />
            <Route element={<RolesTab />} path={toRelativeAdminPath(routes.AdminRoles)} />
            <Route
              element={<ContractTypesTab />}
              path={toRelativeAdminPath(routes.AdminContractTypes)}
            />
            <Route
              element={<IntegrationsList />}
              path={toRelativeAdminPath(routes.AdminIntegrations)}
            />
            <Route element={<BillingPage />} path={toRelativeAdminPath(routes.AdminBilling)} />
            <Route element={<UsagePage />} path={toRelativeAdminPath(routes.AdminUsage)} />
            {/* Admin-only pages: their server resolvers reject non-admins, so gate them here too. */}
            <Route element={<AdminOnlyRoute />}>
              <Route
                element={<UpgradeRequestsPage />}
                path={toRelativeAdminPath(routes.AdminUpgradeRequests)}
              />
              <Route
                element={<DataMigrationPage />}
                path={toRelativeAdminPath(routes.AdminDataMigration)}
              />
            </Route>
            <Route element={<Navigate replace to={link.AdminOrgIndex()} />} path="/" />
          </Routes>
        </div>
      </div>
    </>
  );
}
